// ============================================================================
// stockRestockService — Inventory Reorder & Restock Queue Engine
// ----------------------------------------------------------------------------
// Manages reorder requests when items drop below reorderPoint or minThreshold.
// ============================================================================

import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
  type Timestamp,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { isNoSignInPreviewActive } from '../debug'
import {
  STOCK_RESTOCK_REQUESTS_COLLECTION,
  type StockItem,
  type StockRestockRequest,
  type StockRestockStatus,
} from '../types/stock'
import { emitStockAudit, type AuditContext } from './stockService'
import { receiveRestockRequest } from './stock/stockLedgerService'

const toStockRestockRequest = (id: string, data: DocumentData): StockRestockRequest => ({
  id,
  workspaceId: String(data.workspaceId ?? ''),
  stockItemId: String(data.stockItemId ?? ''),
  stockItemName: data.stockItemName != null ? String(data.stockItemName) : null,
  requestedById: String(data.requestedById ?? ''),
  requestedByName: data.requestedByName != null ? String(data.requestedByName) : null,
  requestedAt: (data.requestedAt as Timestamp | null) ?? null,
  orderedAt: (data.orderedAt as Timestamp | null) ?? null,
  orderedById: data.orderedById != null ? String(data.orderedById) : null,
  orderedByName: data.orderedByName != null ? String(data.orderedByName) : null,
  receivedAt: (data.receivedAt as Timestamp | null) ?? null,
  receivedById: data.receivedById != null ? String(data.receivedById) : null,
  receivedByName: data.receivedByName != null ? String(data.receivedByName) : null,
  receivedQuantity: data.receivedQuantity != null ? Number(data.receivedQuantity) : null,
  status: (data.status as StockRestockStatus) ?? 'pending',
  note: data.note != null ? String(data.note) : null,
})

export const isItemBelowReorderThreshold = (item: StockItem): boolean => {
  const threshold = item.reorderPoint ?? item.minThreshold
  if (threshold == null) return false
  return item.quantity <= threshold
}

export const subscribeRestockRequests = (
  workspaceId: string,
  onData: (requests: StockRestockRequest[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    onData([])
    return () => {}
  }
  if (isNoSignInPreviewActive()) {
    onData([])
    return () => {}
  }

  const coll = collection(db, STOCK_RESTOCK_REQUESTS_COLLECTION)
  const q = query(coll, where('workspaceId', '==', workspaceId))

  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((docSnap) => toStockRestockRequest(docSnap.id, docSnap.data()))
      items.sort((a, b) => {
        const timeA = a.requestedAt?.toMillis() ?? 0
        const timeB = b.requestedAt?.toMillis() ?? 0
        return timeB - timeA
      })
      onData(items)
    },
    (err) => {
      console.warn('[stockRestockService] subscription error', err)
      onError?.(err)
    },
  )
}

export const markOrdered = async (
  requestId: string,
  actor: AuditContext,
  db: Firestore = firestoreDb,
): Promise<void> => {
  const reqRef = doc(db, STOCK_RESTOCK_REQUESTS_COLLECTION, requestId)
  const reqSnap = await getDoc(reqRef)
  if (!reqSnap.exists()) throw new Error(`Restock request ${requestId} not found`)
  const data = reqSnap.data()

  await updateDoc(reqRef, {
    status: 'ordered',
    orderedAt: serverTimestamp(),
    orderedById: actor.actorId,
    orderedByName: actor.actorName ?? null,
  })

  await emitStockAudit(db, String(data.workspaceId), actor, 'stock.reorder.request', String(data.stockItemId), String(data.stockItemName ?? 'Stock item'), {
    requestId,
    status: 'ordered',
  })
}

/**
 * Marks multiple restock requests as ordered all-or-nothing per request with per-id results.
 */
export const markOrderedMany = async (
  requestIds: string[],
  actor: AuditContext,
  db: Firestore = firestoreDb,
): Promise<Record<string, { success: boolean; error?: string }>> => {
  const results: Record<string, { success: boolean; error?: string }> = {}
  for (const requestId of requestIds) {
    try {
      await markOrdered(requestId, actor, db)
      results[requestId] = { success: true }
    } catch (error) {
      results[requestId] = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      }
    }
  }
  return results
}

export interface MarkReceivedInput {
  requestId: string
  receivedQuantity: number
  actor?: AuditContext
  workspaceId?: string
  locationId?: string
  lotCode?: string
  expiryDate?: unknown
  serials?: string[]
}

export const markReceived = async (
  input: MarkReceivedInput,
): Promise<void> => {
  const { requestId, receivedQuantity, workspaceId, locationId, lotCode, expiryDate, serials } = input
  if (receivedQuantity <= 0) throw new Error('receivedQuantity must be positive')

  await receiveRestockRequest({
    requestId,
    receivedQuantity,
    workspaceId,
    locationId,
    lotCode,
    expiryDate,
    serials,
  })
}

export const cancelRequest = async (
  requestId: string,
  actor: AuditContext,
  db: Firestore = firestoreDb,
): Promise<void> => {
  const reqRef = doc(db, STOCK_RESTOCK_REQUESTS_COLLECTION, requestId)
  await updateDoc(reqRef, {
    status: 'cancelled',
    cancelledAt: serverTimestamp(),
    cancelledById: actor.actorId,
  })
}
